import Hls from "hls.js"
import { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useRef, useState } from "react"
import {  PlayerContainer, PlayerWrapper, Video, VideoContainer } from "./Components";
import { PreLoader } from "../PreLoader/PreLoader";
import { WebsiteContext } from "../../context/WebsiteContext";
import { AppContext } from "../../context/AppContext";
import { classNames } from "../../helper";
import { ButtonContainer } from "../BackgroundVideo/Components";
import { Play } from "./Play";
import { MenuPopup } from "../Navigator/Components";
import { useTranslation } from "react-i18next";
import { PlayerNavigator } from "../Navigator/PlayerNavigator";
import { PlayerToolbarContext } from "../../context/PlayerToolbarContext";

export const PMCVideoPlayer = forwardRef(({src, isMuted = false}, ref) => {
    const timeoutRef = useRef(null);
    const {t} = useTranslation();
    const playerRef = useRef(null);
    const hlsRef = useRef(null);
    const isInitializedRef = useRef(false);
    const abortController = useRef(null);
    const isAutoQualityRef = useRef(true);
    const currentQualityRef = useRef(-1);
    const qualityMenuRef = useRef(null);
    const levelsRef = useRef([]);
    const websiteContext = useContext(WebsiteContext);
    const playerToolbarContext = useContext(PlayerToolbarContext);
    const appContext = useContext(AppContext);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isClicked, setIsClicked] = useState(false);
    const [isAutoPlay, setIsAutoPlay] = useState(true);
    const [isBuffering, setIsBuffering] = useState(true);
    const [levels, setLevels] = useState([]);
    const [currentLevel, setCurrentLevel] = useState(-1);
    const [isAutoQuality, setIsAutoQuality] = useState(true);
    const getCurrentQualityText = () => {
        if(!isAutoQualityRef.current) return null;
        if(currentQualityRef.current === -1) return null;
        if(levelsRef.current && levelsRef.current.length > 0 && levelsRef.current[currentQualityRef.current])
            return "("+levelsRef.current[currentQualityRef.current].height+"p)"
        return null;
    };
    const hasMultiQuality = () => {
        if(levelsRef.current && levelsRef.current.length > 1)
            return levelsRef.current[0].height > 0
        return false;
    };
    const CurrentAutoQuality = () => {
        let current = "";
        if(levelsRef.current && levelsRef.current.length > 0 && levelsRef.current[currentQualityRef.current])
            current = "("+levelsRef.current[currentQualityRef.current].height+"p)";
        return t("Auto") + current;
    }
    useImperativeHandle(ref, () => {
        return {
            play: initVideo,
            pause: handlePause,
            remove: handleStop,
            isMute: () => isMuted,
            isPlaying: () => isPlaying,
            isAutoQuality: () => isAutoQualityRef.current,
            hasMultiQuality: () => hasMultiQuality(),
            levels: () => levelsRef.current,
            currentQuality: () => currentQualityRef.current,
            currentQualityText: () => getCurrentQualityText(),
            changeQuality: handleQualityChange,
            showQualityMenu: () => qualityMenuRef.current
        }
    })
    const canAutoPlay = () => {
        if(appContext.isCameFromHome){
            return true;
        }else{
            return isClicked
        }
    }
    useEffect(() => {
        initVideo();
        return () => {
            if (hlsRef.current) {
                hlsRef.current.off(Hls.Events.BUFFER_EMPTY, onBufferEmpty);
                hlsRef.current.off(Hls.Events.FRAG_LOADED, onFragLoaded);
                hlsRef.current.off(Hls.Events.ERROR, onError);
                hlsRef.current.off(Hls.Events.MANIFEST_PARSED, onManifestParsed);
                hlsRef.current.off(Hls.Events.LEVEL_SWITCHED, onLevelSwitched);
            }
            handleStop();
            clearTimeout(timeoutRef.current);
          };
    }, []);
    useEffect(() => {
        if(ref && ref.current)
            websiteContext.initVideoPlayer(ref.current);
    }, [ref]);
    useEffect(() => {
        websiteContext.setIsStreamPlaying(isPlaying);   
    }, [isPlaying]);
    useEffect(() => {
        isAutoQualityRef.current = isAutoQuality;
        currentQualityRef.current = currentLevel;
        levelsRef.current = levels;
        let qualities =  [];
        for(let i = levels.length - 1; i >= 0 ; i--){
            qualities.push({
                index: i,
                level: levels[i]
            })
        }
        qualityMenuRef.current = (
            <MenuPopup className={appContext.language}>
                <h2>{t("Quality")}</h2>
                <ul>
                    <li className={isAutoQuality?"active":""} onClick={(e) => handleQualityChange(-1)}>
                        <CurrentAutoQuality />
                    </li>
                    
                    {qualities.map((quality, index) => {
                        return (
                            <li key={index} 
                                className={!isAutoQuality && currentLevel === quality.index?"active":""}
                                onClick={(e) => handleQualityChange(quality.index)}
                            >
                                {quality.level.height}p
                            </li>
                        )
                    })}
                </ul>
            </MenuPopup>
        )        
    }, [isAutoQuality, currentLevel, levels]);
    
    const initVideo = () => {
        if (isInitializedRef.current) return;
        isInitializedRef.current = true;
        if(playerRef.current && Hls.isSupported()){
            playerRef.current.muted = isMuted;
            playerRef.current.volume = 1;
            hlsRef.current = new Hls();
            hlsRef.current.loadSource(src);
            hlsRef.current.attachMedia(playerRef.current);
            hlsRef.current.on(Hls.Events.BUFFER_EMPTY, onBufferEmpty);
            hlsRef.current.on(Hls.Events.FRAG_LOADED, onFragLoaded);
            hlsRef.current.on(Hls.Events.ERROR, onError);
            hlsRef.current.on(Hls.Events.MANIFEST_PARSED, onManifestParsed);
            hlsRef.current.on(Hls.Events.LEVEL_SWITCHED, onLevelSwitched);
            handlePlay();
        }
    }
    const handleStop = () => {
        setIsPlaying(false);
        const player = playerRef.current;
        hlsRef.current.stopLoad();
        hlsRef.current.destroy();
        if (player) {
            player.removeEventListener('pause', handlePause)
            player.pause();
        }
        
        if(abortController.current){
            abortController.current.abort();
        }
        isInitializedRef.current = false;
        
    }
    const handlePause = () => {
        handleStop();
    }
    const handlePlay = (canPlay = false) => {
        if (!playerRef.current) return;
        if(isPlaying) return;
        setIsPlaying(false);
        setIsBuffering(true);
        if(canPlay || canAutoPlay()){
            playerRef.current.play().then(() => {
                abortController.current = new AbortController();
                setIsPlaying(true);
                setIsBuffering(false);
            }).catch((err) => {
                if(err.name === "NotAllowedError"){
                    setIsBuffering(false);
                    setIsAutoPlay(false);
                }
                setIsPlaying(false);
                
            });
        }else{
            setIsBuffering(false);
            setIsAutoPlay(false);
        }
    }
    useEffect(() => {
        if (playerRef.current) {
          playerRef.current.setAttribute("webkit-playsinline", "true");
          playerRef.current.setAttribute("playsinline", "true");
        }
      }, []);
    useEffect(() => {
        const video = playerRef.current;
        if (!video) return;
        video.addEventListener("pause", handlePause);
        return () => {
          video.removeEventListener("pause", handlePause);
        };
      }, []);
    const onBufferEmpty = () => {
        timeoutRef.current = setTimeout(() => {
          if (playerRef.current.readyState < 3) {
            setIsBuffering(true);
          }
        }, 5000);
      };
    
    const onFragLoaded = () => {
        clearTimeout(timeoutRef.current);
        setIsBuffering(false); 
    };
    
    const onError = (event, data) => {
        if (data?.type === Hls.ErrorTypes.NETWORK_ERROR) {
            setIsBuffering(true);
        }
    };
    const onManifestParsed = (event, data) => {
        setLevels(data.levels);
    }
    const onLevelSwitched = (event_, data) => {
        setCurrentLevel(data.level);
    }
    const handleQualityChange = (levelIndex) => {
        if (hlsRef.current) {
            setIsBuffering(true);
            hlsRef.current.currentLevel = levelIndex;
            setCurrentLevel(levelIndex);
            setIsAutoQuality(levelIndex === -1);
        }
    }
    
    return (
        <PlayerContainer onClick={playerToolbarContext.onClickShowOrHideMenu}>
            <PlayerWrapper>
                {(isBuffering) && <PreLoader show={true} disableBackground={true}/>}
                {!isAutoPlay && <ButtonContainer onClick={(e) => {
                    setIsAutoPlay(true);
                    setIsBuffering(true);
                    setIsClicked(true);
                    handlePlay(true);
                }} className={classNames([!appContext.isLandscape && "rotate"])}><Play /></ButtonContainer>}
                <VideoContainer>
                    <Video ref={playerRef} loop={false} 
                        controls={false} 
                        autoPlay={isAutoPlay}
                        className={classNames([!appContext.isLandscape && "rotate"])}>
                        <p>This browser doesn't support hls</p>
                    </Video>
                    
                </VideoContainer>
                <PlayerNavigator />
            </PlayerWrapper>
        </PlayerContainer>
        
    )
})